import React, { useEffect, useMemo, useState } from "react";
import { useCurrentUserQuery } from "../../../../api/queries/useUserQuery";
import { HTTP_ENDPOINTS, USER_ROLE_NAMES } from "../../../../consts/api";
import { http } from "../../../../services/HttpService";
import { uiNotification } from "../../../../services/UINotificationService";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import styles from "./boomrx.module.scss";

const PAGE_SIZE = 40;

function errorMessage(err, fallback) {
  const data = err && err.response && err.response.data;
  if (data && data.data && data.data.detail) {
    return data.data.detail;
  }
  if (data && data.message && data.message.indexOf("wellsync_") !== 0) {
    return data.message;
  }
  return fallback;
}

function money(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function groupMostExpensive(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const drugName = String(row.drugName || "").trim();
    if (!drugName) return;
    const key = drugName.toLowerCase();
    const price = Number(row.price);
    const next = {
      drugName,
      concentration: String(row.concentration || "").trim(),
      unit: String(row.unit || "").trim(),
      price: Number.isFinite(price) ? price : null,
    };
    const existing = grouped.get(key);
    if (
      !existing ||
      (next.price != null &&
        (existing.price == null || next.price > existing.price))
    ) {
      grouped.set(key, next);
    }
  });
  return Array.from(grouped.values()).sort((a, b) =>
    a.drugName.localeCompare(b.drugName),
  );
}

export function BoomRXPriceList() {
  const { data: user, isLoading: userLoading } = useCurrentUserQuery();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (user?.role !== USER_ROLE_NAMES.admin) return undefined;
    let cancelled = false;
    setLoading(true);
    http
      .get(HTTP_ENDPOINTS.boomrx.priceList())
      .then((res) => {
        if (cancelled) return;
        setList((res.data && res.data.data) || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setList(null);
        uiNotification.error(errorMessage(err, "Failed to load BoomRX prices"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  const rows = list && Array.isArray(list.rows) ? list.rows : [];
  const drugs = useMemo(() => groupMostExpensive(rows), [rows]);
  const needle = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return drugs;
    return drugs.filter((row) => {
      const haystack =
        `${row.drugName} ${row.concentration} ${row.unit}`.toLowerCase();
      return haystack.indexOf(needle) !== -1;
    });
  }, [drugs, needle]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE,
  );

  if (userLoading || user?.role !== USER_ROLE_NAMES.admin) {
    return null;
  }

  return (
    <section className={styles.priceList}>
      <h3 className={styles.dashboardSectionTitle}>Drug price list</h3>
      <p className={styles.priceListLead}>
        Each medication is listed once, at its highest BoomRX price. The clinic
        does not choose the pharmacy or the lower-priced option.
      </p>
      {loading ? (
        <CircularProgress />
      ) : list && list.connected === false ? (
        <p className={styles.dashboardEmpty}>
          Connect a BoomRX clinic to load prices.
        </p>
      ) : (
        <>
          <div className={styles.priceFilters}>
            <input
              className={styles.input}
              type="search"
              value={query}
              placeholder="Search medications"
              aria-label="Search medications"
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(0);
              }}
            />
          </div>
          <p className={styles.priceListCount}>
            {needle
              ? `${filtered.length} of ${drugs.length} medication${
                  drugs.length === 1 ? "" : "s"
                }`
              : `${drugs.length} medication${drugs.length === 1 ? "" : "s"}`}
          </p>
          {visible.length === 0 ? (
            <p className={styles.dashboardEmpty}>
              {needle
                ? "No medications match that search."
                : "No medication prices yet."}
            </p>
          ) : (
            <div className={styles.priceTableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Medication</th>
                    <th>Concentration</th>
                    <th>Highest price</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr
                      key={`${row.drugName}-${row.concentration}-${row.unit}`}
                    >
                      <td>{row.drugName}</td>
                      <td>
                        {row.concentration || "—"}
                        {row.unit ? ` (${row.unit})` : ""}
                      </td>
                      <td>{money(row.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filtered.length > PAGE_SIZE ? (
            <div className={styles.pricePager}>
              <button
                type="button"
                className={styles.btnGhost}
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {safePage + 1} of {pageCount}
              </span>
              <button
                type="button"
                className={styles.btnGhost}
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
